<!--
  AppHeader 组件 - 全局顶栏
  高度模块化，使用子组件和Composable
-->
<template>
  <header class="app-header" :class="{ 'is-scrolled': isScrolled }" role="banner">
    <div class="ah-content">
      <!-- 固定PC端布局 - 移除响应式切换 -->
      <!-- Logo区域 -->
      <Logo />

      <!-- 导航区域 -->
      <Nav :page-list="pageList" />

      <!-- 右侧区域 -->
      <div class="ah-right">
        <!-- API标识 -->
        <ApiBadge @click="$emit('open-api-site')" />

        <div class="ah-controls">
          <!-- 通知铃铛 -->
          <NotificationBell />

          <!-- 批量任务通知徽章 -->
          <el-badge
            v-if="activeTaskCount > 0"
            :value="activeTaskCount"
            :max="99"
            class="batch-task-badge"
          >
            <el-button
              circle
              @click="handleGoToTasks"
              title="查看批量任务"
            >
              <el-icon :class="{ 'spinning-icon': activeTaskCount > 0 }">
                <Loading />
              </el-icon>
            </el-button>
          </el-badge>

          <!-- 用户菜单 -->
          <UserMenu
            :is-logged-in="isLoggedIn"
            :user-info="userInfo"
            :quota-used="getCurrentQuotaUsed"
            :quota-limit="getCurrentQuotaLimit"
            :default-avatar="defaultAvatar"
            :is-dark-theme="isDarkTheme"
            @show-login="emit('show-login')"
            @logout="$emit('logout')"
            @go-to-profile="() => $router.push('/user/overview')"
            @theme-toggle="$emit('theme-toggle')"
            @show-guide="handleShowGuide"
            @open-api-site="$emit('open-api-site')"
          />
        </div>
      </div>
    </div>


  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { Loading } from '@element-plus/icons-vue'
import { useUserStore } from '@/store/user'
import { useBatchStore } from '@/store/batch'
import { useBatchPollingStore } from '@/store/batchPolling'
import { useGuideStore } from '@/store/guide'
import { useTheme } from '@/composables/useTheme'
import { useNavigation } from '@/composables/useNavigation'
import { useScrollFlag } from '@/composables/useScrollFlag'
import { useTTSConfiguration } from '@/composables/useTTSConfiguration'


// 子组件导入
import Logo from './Logo.vue'
import Nav from './Nav.vue'
import ApiBadge from '@/components/ui/ApiBadge.vue'
import UserMenu from '@/components/ui/UserMenu.vue'
import NotificationBell from '@/components/common/NotificationBell.vue'


// 事件定义
const emit = defineEmits<{
  'theme-toggle': []
  'open-api-site': []
  'show-login': []
  'logout': []
}>()


// Store 和 Composables
const router = useRouter()
const userStore = useUserStore()
const batchStore = useBatchStore()
const pollingStore = useBatchPollingStore()
const guideStore = useGuideStore()
const theme = useTheme()
const navigation = useNavigation()
const ttsConfig = useTTSConfiguration()
const { isLoggedIn, currentUser } = storeToRefs(userStore)
const { getCurrentQuotaUsed: _quotaUsed, getCurrentQuotaLimit: _quotaLimit } = ttsConfig

// 响应式计算属性
const isDarkTheme = computed(() => theme.isDarkTheme.value)
const getCurrentQuotaUsed = computed(() => _quotaUsed.value)
const getCurrentQuotaLimit = computed(() => _quotaLimit.value)

// 批量任务相关
const activeTaskCount = computed(() => batchStore.activeTaskCount)

// 转换用户信息格式以匹配 UserMenu 组件的期望类型
const userInfo = computed(() => {
  if (!currentUser.value) return undefined
  return {
    username: currentUser.value.username,
    avatar: currentUser.value.avatar,
    role: currentUser.value.role
  }
})

// 滚动状态
const isScrolled = useScrollFlag(4)



// 页面列表
const pageList = navigation.visiblePages

// 默认头像 - 与用户中心保持一致的风格
const defaultAvatar = computed(() =>
  `https://api.dicebear.com/7.x/micah/svg?seed=${currentUser.value?.username || 'default'}&backgroundColor=b6e3f4`
)

// 显示用户引导
const handleShowGuide = async () => {
  await guideStore.startGuide()
}

// 跳转到转换记录页面（批量任务标签）
const handleGoToTasks = () => {
  router.push({ name: 'tasks', query: { tab: 'batch' } })
}

/**
 * 初始化批量任务状态
 * 只在用户登录时请求轻量级接口，避免游客触发 401 错误
 */
const initBatchTaskStatus = async () => {
  // 游客不请求批量任务接口
  if (!isLoggedIn.value) {
    return
  }

  try {
    // 使用轻量级接口获取活跃会话状态（只返回进行中的任务）
    await batchStore.fetchActiveSessionsStatus()

    // 如果有活跃任务，启动轮询
    if (batchStore.hasActiveTasks) {
      pollingStore.acquire()
    }
  } catch (err) {
    // 静默处理错误，不影响页面加载
    console.warn('[AppHeader] 获取批量任务状态失败:', err)
  }
}

onMounted(() => {
  // 初始化批量任务状态
  initBatchTaskStatus()
})

// 监听登录状态变化，登录后重新获取批量任务状态
watch(isLoggedIn, (newValue, oldValue) => {
  if (newValue && !oldValue) {
    // 用户刚登录，获取批量任务状态
    initBatchTaskStatus()
  } else if (!newValue && oldValue) {
    // 用户退出登录，停止轮询
    pollingStore.release()
  }
})


</script>

<style>
/* AppHeader 组件样式 - 使用设计系统变量 */

.app-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--app-header-h);
  background: var(--ah-bg);
  border-bottom: 1px solid var(--ah-border);
  backdrop-filter: blur(8px);
  z-index: var(--z-app-header);
  transition: all 0.3s ease;
}

.app-header.is-scrolled {
  background: var(--ah-bg-scrolled);
  box-shadow: 0 2px 8px rgba(0,0,0,.1);
}

.ah-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  min-width: 1200px;  /* 增加最小宽度，确保导航和用户信息有足够空间 */
  padding: 0 16px;  /* 与内容区域保持一致的padding */
}

.ah-right {
  display: flex;
  align-items: center;
  gap: var(--ah-gap);
}

.ah-controls {
  display: flex;
  align-items: center;
  gap: var(--ah-gap-sm);
}

/* 批量任务徽章 */
.batch-task-badge {
  :deep(.el-badge__content) {
    background-color: #f56c6c;
    border: 2px solid var(--ah-bg);
  }
}

.spinning-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}


</style>
